/** @file Moves slots off a socket's generated bones onto its parent bone, keeping their attachments in place. */
import type { Draft } from 'immer';
import { affine } from '@pose2d/math/affine';
import type { Affine } from '@pose2d/math/types';
import type { Doc, Id, SocketDoc } from '@pose2d/format/types';
import { gripSetup } from './gripSetup';
import { recomposeAttachment } from './recomposeAttachment';
import { slotAttachments } from './slotAttachments';

/** Local matrix of the socket bone, and of the grip-2 bone below it, relative to the socket's parent bone. */
function boneMatrices(doc: Doc, socket: SocketDoc): Map<Id, Affine> {
  const { setup: s } = gripSetup(socket.a, socket.b);
  const onSocket = affine.fromLocal([1, 0, 0, 1, 0, 0], s.x, s.y, s.rotation, 1, 1, 0, 0);
  const out = new Map<Id, Affine>([[socket.bone, onSocket]]);
  const g = socket.grip2Bone === undefined ? undefined : doc.bones[socket.grip2Bone]?.setup;
  if (socket.grip2Bone === undefined || g === undefined) return out;
  const grip2 = affine.fromLocal([1, 0, 0, 1, 0, 0], g.x, g.y, g.rotation, g.scaleX, g.scaleY, g.shearX, g.shearY);
  out.set(socket.grip2Bone, affine.mul([1, 0, 0, 1, 0, 0], onSocket, grip2));
  return out;
}

/** Recomposes every attachment `slotId` holds that is not in `done` yet, then adds them to `done`. */
function recomposeHeld(draft: Draft<Doc>, slotId: Id, onto: Affine, done: Set<Id>): void {
  for (const attId of slotAttachments(draft, slotId)) {
    const att = done.has(attId) ? undefined : draft.attachments[attId];
    const image = att === undefined ? undefined : draft.images[att.image];
    if (image !== undefined) recomposeAttachment(draft, attId, image, onto);
    done.add(attId);
  }
}

/**
 * For each of `slotIds` on a generated bone of `socket`: recompose its attachments, then move it to socket.parentBone.
 * Limitation: each attachment is recomposed once, for the first listed slot holding it. An attachment also mapped into
 * a slot that stays on another bone, or into slots on both generated bones, then draws in place in that slot only.
 */
export function rehomeSlots(draft: Draft<Doc>, socket: SocketDoc, slotIds: readonly Id[]): void {
  const matrices = boneMatrices(draft, socket);
  const done = new Set<Id>();
  for (const slotId of slotIds) {
    const slot = draft.slots[slotId];
    const onto = slot === undefined ? undefined : matrices.get(slot.bone);
    if (slot === undefined || onto === undefined) continue;
    recomposeHeld(draft, slotId, onto, done);
    slot.bone = socket.parentBone;
  }
}
